import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';



const MindmapContent = ({ session, frameworkPhases, activeSessionId, sessions, setSessions }) => {
  
  const [editMode, setEditMode] = useState({});
  const [newItems, setNewItems] = useState({});

  console.log("Current Session : ", session);
  
  if (!session?.mindmap?.phaseData) {
    return (
      <div className="p-4 bg-white rounded-lg border-gray-300 border-2">
        <p className="text-gray-500 text-center">No mindmap content available yet</p>
      </div>
    );
  }

  const { phaseData } = session.mindmap;
  
  // Helper function to get category colors
  const getCategoryColor = (category) => {
    const colors = {
      skills: 'bg-white text-[#62BB67] border-[#62BB67]',
      knowledge: 'bg-white text-[#378FC0] border-[#378FC0]',
      mindset: 'bg-white text-[#E4A204] border-[#E4A204]',
      tools: 'bg-white text-[#7C1586] border-[#7C1586]',
      abilities: 'bg-white text-gray-800 border-gray-200'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Helper function to get phase color
  const getPhaseColor = (phase) => {
    const colors = {
      empathy: 'bg-white border-gray-300',
      define: 'bg-white border-gray-300',
      ideate: 'bg-white border-gray-300',
      prototype: 'bg-white border-gray-300',
      test: 'bg-white border-gray-300',
      communicate: 'bg-white border-gray-300'
    };
    return colors[phase] || 'bg-gray-50 border-gray-200';
  };

  // Helper function to normalize ability name for key matching
  const normalizeAbilityName = (ability) => {
    return ability.trim();
  };

  // Function to find the actual key in phaseData that matches the ability
  const findAbilityKey = (phaseData, ability, category) => {
    const normalizedAbility = normalizeAbilityName(ability);
    const targetKey = `${normalizedAbility}_${category}`;
    
    // Check if exact match exists
    if (phaseData[targetKey]) {
      return targetKey;
    }
    
    // Try to find with different spacing/formatting
    const keys = Object.keys(phaseData);
    const matchingKey = keys.find(key => {
      const keyWithoutCategory = key.replace(`_${category}`, '');
      return keyWithoutCategory.toLowerCase().trim() === normalizedAbility.toLowerCase().trim();
    });
    
    return matchingKey || targetKey;
  };

  // Function to remove an item
  const removeItem = (phaseKey, category, index, ability = null) => {
    setSessions(prevSessions => {
      return prevSessions.map(session => {
        if (session.id === activeSessionId) {
          const fullKey = ability ? findAbilityKey(session.mindmap.phaseData[phaseKey], ability, category) : category;
          
          if (session?.mindmap?.phaseData?.[phaseKey]?.[fullKey]) {
            const updatedItems = [...session.mindmap.phaseData[phaseKey][fullKey]];
            updatedItems.splice(index, 1);
            
            return {
              ...session,
              mindmap: {
                ...session.mindmap,
                phaseData: {
                  ...session.mindmap.phaseData,
                  [phaseKey]: {
                    ...session.mindmap.phaseData[phaseKey],
                    [fullKey]: updatedItems
                  }
                }
              }
            };
          }
        }
        return session;
      });
    });
  };

  // Function to add a new item
  const addItem = (phaseKey, category, ability = null) => {
    const inputKey = ability ? `${phaseKey}-${normalizeAbilityName(ability)}-${category}` : `${phaseKey}-${category}`;
    const newItem = newItems[inputKey]?.trim();
    
    if (!newItem) return;

    setSessions(prevSessions => {
      return prevSessions.map(session => {
        if (session.id === activeSessionId) {
          if (session?.mindmap?.phaseData?.[phaseKey]) {
            const fullKey = ability ? findAbilityKey(session.mindmap.phaseData[phaseKey], ability, category) : category;
            const currentItems = session.mindmap.phaseData[phaseKey][fullKey] || [];
            
            return {
              ...session,
              mindmap: {
                ...session.mindmap,
                phaseData: {
                  ...session.mindmap.phaseData,
                  [phaseKey]: {
                    ...session.mindmap.phaseData[phaseKey],
                    [fullKey]: [...currentItems, newItem]
                  }
                }
              }
            };
          }
        }
        return session;
      });
    });

    // Clear input and exit edit mode
    setNewItems(prev => ({ ...prev, [inputKey]: '' }));
    setEditMode(prev => ({ ...prev, [inputKey]: false }));
  };

  // Function to handle input change
  const handleInputChange = (inputKey, value) => {
    setNewItems(prev => ({ ...prev, [inputKey]: value }));
  };

  // Function to toggle edit mode
  const toggleEditMode = (inputKey) => {
    setEditMode(prev => ({ ...prev, [inputKey]: !prev[inputKey] }));
    if (!editMode[inputKey]) {
      setNewItems(prev => ({ ...prev, [inputKey]: '' }));
    }
  };

  // Function to handle key press
  const handleKeyPress = (e, phaseKey, category, ability = null) => {
    if (e.key === 'Enter') {
      addItem(phaseKey, category, ability);
    } else if (e.key === 'Escape') {
      const inputKey = ability ? `${phaseKey}-${normalizeAbilityName(ability)}-${category}` : `${phaseKey}-${category}`;
      setEditMode(prev => ({ ...prev, [inputKey]: false }));
      setNewItems(prev => ({ ...prev, [inputKey]: '' }));
    }
  };

  // Component for rendering editable items
  const EditableItemSection = ({ phaseKey, category, items, title, ability = null }) => {
    const inputKey = ability ? `${phaseKey}-${normalizeAbilityName(ability)}-${category}` : `${phaseKey}-${category}`;
    const isEditing = editMode[inputKey];
    const newItemValue = newItems[inputKey] || '';

    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <h7 className="text-sm font-semibold text-gray-600 uppercase tracking-wide block">{title}</h7>
          <button
            onClick={() => toggleEditMode(inputKey)}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
            title={`Add ${title.toLowerCase()}`}
          >
            <Plus size={14} />
          </button>
        </div>
        
        <div className="space-y-1">
          {items && items.map((item, index) => (
            <div key={index} className="group relative inline-block">
              <span className={`inline-block px-2 py-1 text-xs rounded border ${getCategoryColor(category)} mr-1 mb-1`}>
                {item}
                <button
                  onClick={() => removeItem(phaseKey, category, index, ability)}
                  className="ml-1 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                  title="Remove item"
                >
                  <X size={12} />
                </button>
              </span>
            </div>
          ))}
          
          {isEditing && (
            <div className="inline-block mr-1 mb-1">
              <input
                type="text"
                value={newItemValue}
                onChange={(e) => handleInputChange(inputKey, e.target.value)}
                onKeyDown={(e) => handleKeyPress(e, phaseKey, category, ability)}
                onBlur={() => {
                  if (newItemValue.trim()) {
                    addItem(phaseKey, category, ability);
                  } else {
                    toggleEditMode(inputKey);
                  }
                }}
                className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-400"
                placeholder={`Add ${title.toLowerCase()}...`}
                autoFocus
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  // Helper function to get all competencies for a phase (both phase-level and ability-specific)
  const getPhaseCompetencies = (phaseKey, currentPhaseData) => {
    const competencies = {
      skills: [],
      knowledge: [],
      mindset: [],
      tools: []
    };

    // Add phase-level competencies
    ['skills', 'knowledge', 'mindset', 'tools'].forEach(category => {
      if (currentPhaseData[category] && currentPhaseData[category].length > 0) {
        competencies[category] = [...currentPhaseData[category]];
      }
    });

    return competencies;
  };

  // Helper function to get ability-specific competencies
  const getAbilityCompetencies = (phaseKey, currentPhaseData, ability) => {
    const competencies = {
      skills: [],
      knowledge: [],
      mindset: [],
      tools: []
    };

    ['skills', 'knowledge', 'mindset', 'tools'].forEach(category => {
      const key = findAbilityKey(currentPhaseData, ability, category);
      if (currentPhaseData[key] && currentPhaseData[key].length > 0) {
        competencies[category] = currentPhaseData[key];
      }
    });

    return competencies;
  };

  return (
    <div className="mt-6 space-y-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Design Competencies</h3>
      
      {frameworkPhases.map(phaseName => {
        const phaseKey = phaseName.toLowerCase();
        const currentPhaseData = phaseData[phaseKey];
        
        // Skip phases that have no data
        if (!currentPhaseData || !currentPhaseData.abilities || currentPhaseData.abilities.length === 0) {
          return null;
        }

        const phaseCompetencies = getPhaseCompetencies(phaseKey, currentPhaseData);
        const hasPhaseCompetencies = Object.values(phaseCompetencies).some(items => items.length > 0);

        return (
          <div key={phaseKey} className={`p-4 rounded-lg border-2 ${getPhaseColor(phaseKey)}`}>
            <h4 className="text-xl font-bold text-gray-800 mb-3 capitalize">{phaseName} Phase</h4>
            
            {/* Abilities Section */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Abilities</h5>
                <button
                  onClick={() => toggleEditMode(`${phaseKey}-abilities`)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  title="Add ability"
                >
                  <Plus size={14} />
                </button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {currentPhaseData.abilities.map((ability, index) => (
                  <div key={index} className="group relative">
                    <span className="px-3 py-1 bg-white text-gray-500 rounded-full text-sm font-medium border border-gray-400">
                      {ability}
                      <button
                        onClick={() => removeItem(phaseKey, 'abilities', index)}
                        className="ml-2 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                        title="Remove ability"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  </div>
                ))}
                
                {editMode[`${phaseKey}-abilities`] && (
                  <div className="inline-block">
                    <input
                      type="text"
                      value={newItems[`${phaseKey}-abilities`] || ''}
                      onChange={(e) => handleInputChange(`${phaseKey}-abilities`, e.target.value)}
                      onKeyDown={(e) => handleKeyPress(e, phaseKey, 'abilities')}
                      onBlur={() => {
                        const inputKey = `${phaseKey}-abilities`;
                        if (newItems[inputKey]?.trim()) {
                          addItem(phaseKey, 'abilities');
                        } else {
                          toggleEditMode(inputKey);
                        }
                      }}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-full focus:outline-none focus:border-blue-400"
                      placeholder="Add ability..."
                      autoFocus
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Phase-level Competencies */}
            {hasPhaseCompetencies && (
              <div className="mb-6">
                <h6 className="text-lg font-semibold text-gray-700 mb-3 border-b border-gray-300 pb-1">
                  Phase Competencies
                </h6>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Skills */}
                  {phaseCompetencies.skills.length > 0 && (
                    <EditableItemSection
                      phaseKey={phaseKey}
                      category="skills"
                      items={phaseCompetencies.skills}
                      title="Skills"
                    />
                  )}

                  {/* Knowledge */}
                  {phaseCompetencies.knowledge.length > 0 && (
                    <EditableItemSection
                      phaseKey={phaseKey}
                      category="knowledge"
                      items={phaseCompetencies.knowledge}
                      title="Knowledge"
                    />
                  )}

                  {/* Mindset */}
                  {phaseCompetencies.mindset.length > 0 && (
                    <EditableItemSection
                      phaseKey={phaseKey}
                      category="mindset"
                      items={phaseCompetencies.mindset}
                      title="Mindset"
                    />
                  )}

                  {/* Tools */}
                  {phaseCompetencies.tools.length > 0 && (
                    <EditableItemSection
                      phaseKey={phaseKey}
                      category="tools"
                      items={phaseCompetencies.tools}
                      title="Instruments"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Ability-specific Competencies */}
            {currentPhaseData.abilities.map((ability, abilityIndex) => {
              const abilityCompetencies = getAbilityCompetencies(phaseKey, currentPhaseData, ability);
              const hasAbilityCompetencies = Object.values(abilityCompetencies).some(items => items.length > 0);
              
              if (!hasAbilityCompetencies) return null;

              return (
                <div key={abilityIndex} className="mb-6">
                  <h6 className="text-lg font-semibold text-gray-700 mb-3 border-b border-gray-300 pb-1">
                    {ability} Competencies
                  </h6>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Skills */}
                    {abilityCompetencies.skills.length > 0 && (
                      <EditableItemSection
                        phaseKey={phaseKey}
                        category="skills"
                        items={abilityCompetencies.skills}
                        title="Skills"
                        ability={ability}
                      />
                    )}

                    {/* Knowledge */}
                    {abilityCompetencies.knowledge.length > 0 && (
                      <EditableItemSection
                        phaseKey={phaseKey}
                        category="knowledge"
                        items={abilityCompetencies.knowledge}
                        title="Knowledge"
                        ability={ability}
                      />
                    )}

                    {/* Mindset */}
                    {abilityCompetencies.mindset.length > 0 && (
                      <EditableItemSection
                        phaseKey={phaseKey}
                        category="mindset"
                        items={abilityCompetencies.mindset}
                        title="Mindset"
                        ability={ability}
                      />
                    )}

                    {/* Tools */}
                    {abilityCompetencies.tools.length > 0 && (
                      <EditableItemSection
                        phaseKey={phaseKey}
                        category="tools"
                        items={abilityCompetencies.tools}
                        title="Instruments"
                        ability={ability}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      
      {/* Summary Section */}
      <div className="p-4 bg-white rounded-lg border-2 border-gray-300">
        <h4 className="text-lg font-semibold text-gray-800 mb-3">Project Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-2 gap-4 text-center">
          <div className="p-3 bg-white rounded border">
            <div className="text-2xl font-bold text-green-600">
              {Object.values(phaseData).reduce((total, phase) => 
                total + (phase.abilities ? phase.abilities.length : 0), 0
              )}
            </div>
            <div className="text-sm text-gray-600">Total Abilities</div>
          </div>
          <div className="p-3 bg-white rounded border">
            <div className="text-2xl font-bold text-blue-600">
              {Object.keys(phaseData).filter(key => 
                phaseData[key].abilities && phaseData[key].abilities.length > 0
              ).length}
            </div>
            <div className="text-sm text-gray-600">Active Phases</div>
          </div>
        </div>
      </div>

      <p className="text-xs mx-auto font-bold text-gray-300">© 2025 All rights Reserved <span className="text-xs font-normal font-ethnocentric">21st Century Edutech LLC</span>.</p>
    </div>
  );
};

export default MindmapContent;